import type { HoldCollection, HoldGroup, HoldPayload } from '@holdthat/api';

export type { AuthSession, HoldCollection, HoldComment, HoldGroup, HoldGroupMember, HoldNotification, HoldPayload } from '@holdthat/api';
export type HoldKind = HoldPayload['kind'];
export type TimelineStyle = 'cards' | 'compact';

export interface HoldAttachmentDraft {
  metadata: HoldPayload['attachments'][number];
  blob: Blob;
}

export const MAX_ATTACHMENTS = 10;
export const MAX_ATTACHMENT_BYTES = 100 * 1024 * 1024;

export function normalizeSearch(value: string): string {
  return value.trim().normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase('de');
}

export function allTags(hold: HoldPayload) {
  const seen = new Set<string>();
  return [...hold.manualTags, ...hold.automaticTags].filter((tag) => {
    const key = normalizeSearch(tag.text);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function displayTitle(hold: HoldPayload): string {
  const textLine = hold.textContent.split(/\r?\n/).find((line) => line.trim())?.trim();
  let host = '';
  try { host = new URL(hold.urlString).hostname.replace(/^www\./, ''); } catch { /* not a URL */ }
  return hold.title.trim() || textLine || hold.attachments[0]?.filename || host || kindLabel(hold.kind);
}

export function matchesHold(hold: HoldPayload, query: string): boolean {
  const wanted = normalizeSearch(query);
  if (!wanted) return true;
  return normalizeSearch([
    hold.title, hold.holdDescription, hold.source, kindLabel(hold.kind), hold.notes,
    hold.urlString, hold.textContent, ...hold.attachments.map((file) => file.filename),
    ...allTags(hold).map((tag) => tag.text),
  ].join(' ')).includes(wanted);
}

export function hasTag(hold: HoldPayload, tag: string): boolean {
  const wanted = normalizeSearch(tag);
  return allTags(hold).some((item) => normalizeSearch(item.text) === wanted);
}

export function inferKind(url: string, text: string, attachments: HoldPayload['attachments'] = []): HoldKind {
  const type = attachments[0]?.contentType.toLocaleLowerCase();
  if (type?.startsWith('image/')) return 'photo';
  if (type === 'application/pdf') return 'document';
  if (type?.startsWith('audio/')) return 'audio';
  if (type && ['text/vcard', 'text/x-vcard', 'text/directory'].includes(type)) return 'contact';
  if (type) return 'file';
  if (text.trim()) return 'text';
  return isMapUrl(url) ? 'location' : 'link';
}

export function detectSource(value: string): HoldPayload['source'] {
  let host = '';
  try { host = new URL(value).hostname.toLocaleLowerCase(); } catch { return 'unknown'; }
  if (host.includes('instagram.com')) return 'instagram';
  if (host.includes('tiktok.com')) return 'tiktok';
  if (host.includes('youtube.com') || host === 'youtu.be') return 'youtube';
  if (host.includes('reddit.com') || host === 'redd.it') return 'reddit';
  if (host === 'x.com' || host.includes('twitter.com')) return 'x';
  return host ? 'web' : 'unknown';
}

export function isMapUrl(value: string): boolean {
  try {
    const url = new URL(value);
    const host = url.hostname.toLocaleLowerCase();
    const path = url.pathname.toLocaleLowerCase();
    return host === 'maps.apple.com' || host === 'maps.google.com' || host === 'maps.app.goo.gl'
      || (host === 'goo.gl' && path.includes('maps')) || (host.includes('google.') && path.includes('/maps'));
  } catch { return false; }
}

export function kindLabel(kind: HoldKind): string {
  switch (kind) {
    case 'link': return 'Link';
    case 'photo': return 'Foto';
    case 'document': return 'Dokument';
    case 'text': return 'Text';
    case 'file': return 'Datei';
    case 'location': return 'Ort';
    case 'contact': return 'Kontakt';
    case 'audio': return 'Audio';
  }
}

export function sourceLabel(source: string): string {
  return ({ instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube', reddit: 'Reddit', x: 'X', safari: 'Safari', web: 'Web', unknown: 'Unbekannt' } as Record<string, string>)[source] ?? source;
}

export function newTag(text: string, automatic = false) {
  return { id: crypto.randomUUID(), text: text.trim(), isAutomatic: automatic, confidence: null };
}

const automaticTagRules: [string, string[]][] = [
  ['Rezept', ['rezept', 'recipe', 'zutaten', 'ingredients', 'kochen', 'cooking']],
  ['Pasta', ['pasta', 'spaghetti', 'lasagne', 'nudeln']],
  ['Fitness', ['fitness', 'workout', 'training', 'gym']],
  ['Mobility', ['mobility', 'beweglichkeit', 'stretching']],
  ['Reise', ['reise', 'travel', 'urlaub', 'vacation', 'hotel']],
  ['Japan', ['japan', 'tokio', 'tokyo', 'kyoto']],
  ['Technik', ['technik', 'technology', 'gadget', 'software', 'hardware']],
  ['KI', ['kunstliche intelligenz', 'artificial intelligence', 'machine learning', 'chatgpt', ' ai ']],
  ['Business', ['business', 'startup', 'marketing', 'sales', 'unternehmen']],
  ['DIY', ['diy', 'selbermachen', 'do it yourself', 'tutorial']],
  ['Restaurant', ['restaurant', 'cafe', 'food', 'essen']],
  ['Musik', ['musik', 'music', 'song', 'album', 'playlist']],
  ['Podcast', ['podcast', 'episode']],
];

export function automaticTagsFor(hold: Pick<HoldPayload, 'kind' | 'title' | 'holdDescription' | 'notes' | 'urlString' | 'textContent' | 'attachments'>) {
  const haystack = ` ${normalizeSearch([hold.title, hold.holdDescription, hold.notes, hold.urlString, hold.textContent, ...hold.attachments.map((item) => item.filename)].join(' '))} `;
  const tags = automaticTagRules.flatMap(([text, keywords]) => {
    const hits = keywords.filter((keyword) => haystack.includes(normalizeSearch(keyword))).length;
    return hits ? [{ ...newTag(text, true), confidence: Math.min(.95, .58 + (hits - 1) * .12) }] : [];
  });
  if (hold.kind !== 'link') tags.unshift({ ...newTag(hold.kind === 'document' && hold.attachments[0]?.contentType === 'application/pdf' ? 'PDF' : kindLabel(hold.kind), true), confidence: 1 });
  return tags;
}

export function createHold(input: Partial<HoldPayload> & Pick<HoldPayload, 'urlString' | 'textContent'>): HoldPayload {
  const now = new Date().toISOString();
  const attachments = input.attachments ?? [];
  const hold: HoldPayload = {
    id: input.id ?? crypto.randomUUID(), ownerID: input.ownerID ?? null,
    urlString: input.urlString.trim(), kind: input.kind ?? inferKind(input.urlString, input.textContent, attachments),
    textContent: input.textContent.trim(), attachments, title: input.title?.trim() ?? '',
    holdDescription: input.holdDescription?.trim() ?? '', thumbnailURLString: input.thumbnailURLString ?? null,
    thumbnailAttachmentID: input.thumbnailAttachmentID ?? null,
    source: input.source ?? detectSource(input.urlString), notes: input.notes?.trim() ?? '',
    manualTags: input.manualTags ?? [], automaticTags: input.automaticTags ?? [],
    createdAt: input.createdAt ?? now, updatedAt: now, isFavorite: input.isFavorite ?? false,
    isArchived: input.isArchived ?? false, isDeleted: input.isDeleted ?? false,
    collectionIDs: input.collectionIDs ?? [], groupIDs: input.groupIDs ?? [], needsSync: true,
  };
  if (!input.automaticTags) hold.automaticTags = automaticTagsFor(hold);
  return hold;
}

export function createCollection(name: string): HoldCollection {
  const now = new Date().toISOString();
  return { id: crypto.randomUUID(), name: name.trim(), symbolName: 'folder.fill', createdAt: now, updatedAt: now, isDeleted: false, needsSync: true };
}

export function isOwner(hold: HoldPayload, userId?: string): boolean {
  return !hold.ownerID || hold.ownerID === userId;
}

export function groupForId(groups: HoldGroup[], id: string) { return groups.find((group) => group.id === id); }
